import { ApolloLink, split, HttpLink, createHttpLink } from '@apollo/client';
import { ApolloClient } from '@apollo/client-integration-nextjs';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';

import {
  getUserFromCookie,
  getUserAccessTokenCookie,
  getUserRefreshTokenCookie,
  setUserAccessTokenCookie,
  setUserRefreshTokenCookie,
} from 'commons/localStorage';
import isDevServer from 'commons/isDevServer';
import isLocalhost from 'commons/isLocalhost';
import getAlexandriaUrl from 'commons/getAlexandriaUrl';

import isClient from 'commons/isClient';
import getEnvValue from '@/commons/getEnvValue';
import {
  classifyFetchFailure,
  FETCH_WITH_RETRY_ERROR_PREFIX,
} from '@/utils/classifyFetchFailure';
import { fetchWithRetry } from '@/utils/fetchWithRetry';
import { isPageTeardownError } from '@/utils/pageLifecycle';
import { logError } from '@/utils/logger.mjs';
import getCacheStrategy from '@/utils/server/getCacheStrategy';
import getSessionKey from 'commons/getSessionKey';
import { resolvers, createCache } from './apolloCache';
import createSessionLink from './createSessionLink';

// Client ended the request; 499 is already filtered from Sentry (CON-8173).
const isClientClosedRequest = err =>
  err?.name === 'AbortError' || isPageTeardownError(err);

const CLIENT_CLOSED_REQUEST_STATUS = 499;
const CLIENT_CLOSED_REQUEST_HEADER = 'x-joybird-client-closed';

const clientClosedRequestResponse = () =>
  new Response(null, {
    status: CLIENT_CLOSED_REQUEST_STATUS,
    statusText: 'Client Closed Request',
    headers: { [CLIENT_CLOSED_REQUEST_HEADER]: '1' },
  });

// Tagged so a real upstream 499 is still reported rather than swallowed.
const isOwnClientClosedResponse = networkError =>
  networkError?.statusCode === CLIENT_CLOSED_REQUEST_STATUS &&
  networkError?.response?.headers?.get(CLIENT_CLOSED_REQUEST_HEADER) === '1';

// HttpLink lowercases header names, so the token is replaced here rather than
// sent as a second Authorization header, which Alexandria can't verify. A
// missing token can be stored as 'undefined', as authLink also checks.
const withAccessToken = (options, accessToken) => {
  const { authorization, Authorization, ...headers } = options.headers ?? {};

  return {
    ...options,
    headers:
      accessToken && accessToken !== 'undefined'
        ? { ...headers, authorization: accessToken }
        : headers,
  };
};

// The token refresh finished after its session ended or changed
const SESSION_CHANGED = Symbol('session changed');

const makeApolloClient = logoutClient => () => {
  const cache = createCache();
  let refreshTokenPromise = null;

  // custom request fetcher for refresh token
  const requestFetch = async (uri, options) => {
    const sessionAtSend = getSessionKey();

    try {
      // Determine cache strategy based on request context
      const cacheStrategy = getCacheStrategy(options, 'force-cache');

      const response = await fetchWithRetry(uri, {
        ...options,
        cache: cacheStrategy,
        revalidate: 3600,
      });

      if (!response.ok) {
        return response;
      }

      const responseClone = response.clone(); // Clone only when the response body will be read
      const json = await response.json();
      const finalExtensions =
        json && Array.isArray(json) ? json[0].extensions : json.extensions;
      // If sent JWToken is expired
      if (
        (finalExtensions && finalExtensions.meta.tokenExpired) ||
        (getUserFromCookie() &&
          getUserRefreshTokenCookie() &&
          !getUserAccessTokenCookie())
      ) {
        // The session changed while this request was in flight, so it must not
        // start a refresh with the new session's cookies; the session link
        // sends it again for the current user, or fails it (CON-8904)
        if (getSessionKey() !== sessionAtSend) {
          return responseClone;
        }

        if (!refreshTokenPromise) {
          const user = getUserFromCookie();
          const refreshToken = getUserRefreshTokenCookie();
          if (user && refreshToken) {
            const isSameSession = () =>
              getUserFromCookie()?.id === user.id &&
              getUserRefreshTokenCookie() === refreshToken;

            refreshTokenPromise = fetchWithRetry(getAlexandriaUrl(), {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                operationName: 'refreshToken',
                query: `mutation refreshToken($input: RefreshTokenInput) {
                  refreshToken(input: $input) {
                    error
                    access_token
                    refresh_token
                  }
                }`,
                variables: {
                  input: {
                    userId: user.id,
                    userEmail: user.email,
                    userFirstName: user.first_name,
                    userLastName: user.last_name,
                    userGroupId: user.group_id,
                    refreshToken,
                  },
                },
              }),
              cache: 'no-store', // Don't cache token refresh requests
            })
              .then(async refreshResponse =>
                refreshResponse.ok
                  ? (await refreshResponse.json())?.data?.refreshToken
                  : null
              )
              .then(tokens => {
                // The session can end or change while the refresh is in
                // flight; only the session that asked may get its tokens or be
                // logged out by it (CON-8904)
                if (!isSameSession()) {
                  // Another tab may have refreshed the same user's tokens
                  return getSessionKey() === user.id
                    ? getUserAccessTokenCookie()
                    : SESSION_CHANGED;
                }

                if (tokens?.access_token && tokens?.refresh_token) {
                  setUserRefreshTokenCookie(tokens.refresh_token);
                  setUserAccessTokenCookie(tokens.access_token);
                  return tokens.access_token;
                }

                logoutClient();
                return undefined;
              });
          } else {
            logoutClient();
          }
        }
        if (refreshTokenPromise) {
          return refreshTokenPromise.then(async newAccessToken => {
            refreshTokenPromise = null;

            if (newAccessToken === SESSION_CHANGED) {
              return responseClone;
            }

            const retryOptions = withAccessToken(options, newAccessToken);
            // Determine cache strategy based on request context
            const retryCacheStrategy = getCacheStrategy(
              retryOptions,
              'force-cache'
            );
            try {
              return await fetchWithRetry(uri, {
                ...retryOptions,
                cache: retryCacheStrategy,
                revalidate: 3600,
              });
            } catch (err) {
              if (isClientClosedRequest(err)) {
                return clientClosedRequestResponse();
              }
              throw err;
            }
          });
        }
      }
      return responseClone;
    } catch (err) {
      if (isClientClosedRequest(err)) {
        return clientClosedRequestResponse();
      }
      throw err;
    }
  };

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    try {
      if (graphQLErrors) {
        graphQLErrors.forEach(err => {
          logError(
            new Error(
              `GraphQL Error${
                operation?.operationName ? `: ${operation?.operationName}` : ''
              }${err?.message ? `: ${err?.message}` : ''}`,
              { cause: err }
            )
          );
        });
      }

      if (networkError) {
        const netMsg = networkError?.message || '';
        if (netMsg.startsWith(FETCH_WITH_RETRY_ERROR_PREFIX)) {
          return;
        }

        if (isOwnClientClosedResponse(networkError)) {
          return;
        }

        const {
          fetchFailureClass,
          causeCode,
          causeChainSummary,
        } = classifyFetchFailure(
          networkError,
          networkError.statusCode != null ? networkError.statusCode : undefined
        );
        const cause = networkError?.cause || networkError;

        logError(
          new Error(`Network error${netMsg ? `: ${netMsg}` : ''}`, {
            cause: networkError,
            context: operation,
          }),
          {
            graphql_operation: operation?.operationName || 'unknown',
            fetchFailureClass,
            // CON-9073: the status filters Sentry issues and drives the
            // crawler 429/503 sampling in instrumentation-client.js.
            ...(networkError.statusCode != null && {
              httpStatus: String(networkError.statusCode),
            }),
          },
          {
            underlying_error: cause?.name,
            underlying_message: cause?.message,
            causeCode: causeCode || undefined,
            causeChainSummary,
          }
        );
      }
    } catch (err) {
      // If an error occurs while reporting errors to Sentry, just output to
      // logs instead. logError() reports to Sentry, which is exactly what
      // just failed here, so using it would risk recursing.
      // eslint-disable-next-line no-console
      console.error(
        new Error('Error reporting Apollo Client error to Sentry', {
          cause: err,
        })
      );
    }
  });

  // Add Authentication Header to Apollo client request
  const authLink = new ApolloLink((operation, forward) => {
    const accessToken = getUserAccessTokenCookie();

    if (accessToken) {
      const headers = {};
      if (accessToken && accessToken !== 'undefined') {
        headers.Authorization = accessToken;
      }
      if (Object.keys(headers).length > 0) {
        operation.setContext(({ headers: previousHeaders }) => ({
          headers: { ...previousHeaders, ...headers },
        }));
      }
    }

    return forward(operation);
  });

  let httpLinks = [];
  let batchHttpLinks = [];
  let apolloConfig = {};

  const shouldCreateBatchHttpLink = isClient();
  if (shouldCreateBatchHttpLink) {
    // Enables batching of all queries till noBatch context value is encountered
    const batchHttpLink = new BatchHttpLink({
      uri: getAlexandriaUrl(),
      credentials: 'include',
      fetch: requestFetch,
      batchMax: 1,
    });
    // Before authLink, so a query sent again gets the current user's token
    const sessionLink = createSessionLink(getSessionKey);
    httpLinks = ApolloLink.from([
      errorLink,
      sessionLink,
      authLink,
      new HttpLink({
        uri: getAlexandriaUrl(),
        credentials: 'include',
        fetch: requestFetch,
      }),
    ]);
    batchHttpLinks = ApolloLink.from([
      errorLink,
      sessionLink,
      authLink,
      batchHttpLink,
    ]);
    apolloConfig = {
      assumeImmutableResults: true,
      link: split(
        operation => operation.getContext().noBatch === true,
        httpLinks,
        batchHttpLinks
      ),
      cache,
      connectToDevTools:
        getEnvValue('NEXT_PUBLIC_BUILD_MODE') === 'development',
      // shouldBatch: true,
      resolvers,
    };
  } else {
    apolloConfig = {
      assumeImmutableResults: true,
      link: ApolloLink.from([
        errorLink,
        authLink,
        createHttpLink({
          uri: getAlexandriaUrl(),
          credentials: 'same-origin',
          fetch: requestFetch,
        }),
      ]),
      cache,
      resolvers,
    };
  }

  const client = new ApolloClient({
    ...apolloConfig,
    connectToDevTools: isLocalhost() || isDevServer(),
  });

  return client;
};

export default makeApolloClient;
